import { descend, sort } from 'ramda';

export interface DocumentFileSummary {
	id: number;
	filename: string;
	purpose?: string | null;
	created?: string;
}

export interface ClassifiedDocuments<T extends DocumentFileSummary = DocumentFileSummary> {
	photos: T[];
	quotations: T[];
	invoices: T[];
	reports: T[];
	supportingDocuments: T[];
	latestDocuments: T[];
}

export interface ClassifyDocumentFilesOptions {
	/** When true, Photos = purpose "Photo" only. When false, matches store reducer (filename .jpg/.png). */
	purposeBasedPhotos?: boolean;
}

export function coerceDocumentFilesArray<T>(
	arrOrObj: T[] | { files?: T[]; data?: T[] } | null | undefined
): T[] {
	if (Array.isArray(arrOrObj)) {
		return arrOrObj;
	}
	return (arrOrObj?.files ?? arrOrObj?.data ?? []) as T[];
}

export function normalizeDocumentPurpose(p: string | null | undefined): string {
	return (p ?? '').trim().toLowerCase().replace(/\s+/g, '_');
}

function purposeIncludes(purpose: string | null | undefined, fragment: string): boolean {
	return (purpose ?? '').toLowerCase().includes(fragment);
}

function dedupeById<T extends DocumentFileSummary>(files: T[]): T[] {
	const seen = new Set<number>();
	return files.filter(f => {
		if (f.id == null || seen.has(f.id)) {
			return false;
		}
		seen.add(f.id);
		return true;
	});
}

function withPdfFilename<T extends DocumentFileSummary>(file: T): T {
	if (!file.filename?.includes('.')) {
		return { ...file, filename: `${file.filename}.pdf` };
	}
	return file;
}

function latestByCreated<T extends DocumentFileSummary>(files: T[]): T | undefined {
	if (!files.length) {
		return undefined;
	}
	return sort(descend((f: T) => new Date(f.created ?? 0)), files)[0];
}

/**
 * Splits get_files results into explicit sections plus a catch-all supportingDocuments bucket.
 * Each file appears in exactly one primary section (or supportingDocuments).
 */
export function classifyDocumentFiles<T extends DocumentFileSummary>(
	files: T[],
	options: ClassifyDocumentFilesOptions = {}
): ClassifiedDocuments<T> {
	const { purposeBasedPhotos = true } = options;
	const allFiles = files ?? [];

	const quotations = dedupeById(
		allFiles.filter(f => purposeIncludes(f.purpose, 'quotation') || purposeIncludes(f.purpose, 'quote'))
	).map(withPdfFilename);

	const invoices = allFiles.filter(f => purposeIncludes(f.purpose, 'invoice')).map(withPdfFilename);

	const photos = dedupeById(
		purposeBasedPhotos
			? allFiles.filter(f => normalizeDocumentPurpose(f.purpose) === 'photo')
			: [
					...allFiles.filter(f => f.filename?.toLowerCase().includes('.jpg')),
					...allFiles.filter(f => f.filename?.toLowerCase().includes('.png'))
				]
	);

	const agreementOfLoss = allFiles.filter(f => purposeIncludes(f.purpose, 'agreement of loss')).map(withPdfFilename);

	const reports = dedupeById([
		...allFiles.filter(
			f =>
				['report', 'coc_certificate', 'repudiation_letter'].some(p => purposeIncludes(f.purpose, p)) &&
				!purposeIncludes(f.purpose, 'quot')
		),
		...agreementOfLoss
	]).map(withPdfFilename);

	const explicitIds = new Set(
		[...photos, ...quotations, ...invoices, ...reports].map(f => f.id).filter(id => id != null)
	);

	const supportingDocuments = allFiles.filter(f => !explicitIds.has(f.id));

	const latestDocuments: T[] = [];
	const latestReport = latestByCreated(reports);
	const latestInvoice = latestByCreated(invoices);
	const latestQuotation = latestByCreated(quotations);
	if (latestReport) {
		latestDocuments.push(latestReport);
	}
	if (latestInvoice) {
		latestDocuments.push(latestInvoice);
	}
	if (latestQuotation) {
		latestDocuments.push(latestQuotation);
	}

	return {
		photos,
		quotations,
		invoices,
		reports,
		supportingDocuments,
		latestDocuments
	};
}
